import { TYPE_CONTRACTS, TYPE_DECISIONS, TYPE_ISSUES, TYPE_PROCUREMENTS, TYPE_RISKS } from './homeSectionSummary';

import { formatDateWithYear } from 'Helpers/dateTimeService';

/**
 * The cards in "Needs your attention", derived from the payload the tiles
 * already read.
 *
 * A PURE FUNCTION OF `counts` AND `deltas`, AND IT MUST STAY ONE. There is no
 * attention endpoint and no attention resolver, because a second aggregation
 * over the same records is a second set of figures to keep in step with the
 * tiles beneath them — and the first time they disagreed, the page would be
 * telling the user two different things about one record. Every figure here is
 * a key the server already computed for a tile.
 *
 * A CARD IS HIDDEN AT ZERO, and absent at no capability. Both fall out of the
 * same read: the server omits a delta key entirely for a user who may not see
 * it, so `figure()` returns null for them and null fails the `> 0` test the
 * same way a 0 does. This strip is a list of things going wrong; a card
 * reading "0 overdue" is not one of them.
 *
 * THE ORDER IS FIXED AND DOES NOT SORT BY SEVERITY. A strip that reorders
 * itself as the numbers move means a user cannot learn where their own
 * exception lives, and re-reads the whole row every morning.
 */
export const ATTENTION_APPROVALS = 'approvals';

const figure = (value) => (typeof value === 'number' ? value : null);

/**
 * How loudly a card reads.
 *
 * RED IS RESERVED FOR LATENESS — a date that has already passed, or a risk the
 * register itself scores red. Amber is "waiting on you", which is not the same
 * claim and must not borrow the same colour, or nothing on the strip means
 * anything in particular.
 */
const TONE_RED = 'red';
const TONE_AMBER = 'amber';

/**
 * @returns {Array<{key: string, type: string, label: string, value: number, detail: string|null, tone: string}>}
 *   one entry per non-zero exception, in the fixed order above. Empty when the
 *   user has none, which is what hides the whole strip.
 */
export const attentionCards = (counts = {}, deltas = {}) => {
    const cards = [];

    const approvals = figure(counts.approvals);

    if (approvals > 0) {
        const oldest = figure(deltas.approvals_oldest_days);
        const overThreshold = figure(deltas.approvals_waiting_over_threshold);

        cards.push({
            key: ATTENTION_APPROVALS,
            type: ATTENTION_APPROVALS,
            label: $t('Awaiting your approval'),
            value: approvals,
            /* "Oldest waiting 0 days" is true and reads like a bug — everything
               in the queue arrived today, which the number beside it already
               says. The clause is dropped rather than reworded. */
            detail: oldest > 0 ? $t('Oldest waiting {{count}} days', { count: oldest }) : null,
            /* Red only once something has actually breached the named SLA, not
               merely because an approval is waiting. */
            tone: overThreshold > 0 ? TONE_RED : TONE_AMBER,
        });
    }

    const overdueToMarket = figure(deltas.procurements_overdue_to_market);

    if (overdueToMarket > 0) {
        const oldestDate = deltas.procurements_oldest_to_market_date;

        cards.push({
            key: TYPE_PROCUREMENTS,
            type: TYPE_PROCUREMENTS,
            label: $t('Procurements overdue to market'),
            value: overdueToMarket,
            /* The date is a separate key and can be absent while the count is
               present — with no default procurement status configured the count
               is a truthful 0 and there is no oldest item to name. */
            detail: oldestDate ? $t('Oldest due {{date}}', { date: formatDateWithYear(oldestDate) }) : null,
            tone: TONE_RED,
        });
    }

    const unsigned = figure(deltas.contracts_unsigned);

    if (unsigned > 0) {
        cards.push({
            key: TYPE_CONTRACTS,
            type: TYPE_CONTRACTS,
            label: $t('Contracts not signed'),
            value: unsigned,
            detail: $t('{{count}} awaiting signature', { count: unsigned }),
            tone: TONE_AMBER,
        });
    }

    const red = figure(deltas.risks_red);

    if (red > 0) {
        cards.push({
            key: TYPE_RISKS,
            type: TYPE_RISKS,
            label: $t('Risks rated red'),
            value: red,
            detail: $t('{{count}} rated red', { count: red }),
            tone: TONE_RED,
        });
    }

    const issues = figure(deltas.issues_high_priority);

    if (issues > 0) {
        cards.push({
            key: TYPE_ISSUES,
            type: TYPE_ISSUES,
            label: $t('High priority issues'),
            value: issues,
            detail: $t('{{count}} high priority', { count: issues }),
            tone: TONE_AMBER,
        });
    }

    const decisions = figure(deltas.decisions_high_priority);

    if (decisions > 0) {
        cards.push({
            key: TYPE_DECISIONS,
            type: TYPE_DECISIONS,
            label: $t('High priority decisions'),
            value: decisions,
            detail: $t('{{count}} high priority', { count: decisions }),
            tone: TONE_AMBER,
        });
    }

    return cards;
};

export { TONE_AMBER, TONE_RED };
